import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Users,
  TrendingUp,
  Clock,
  Zap,
  Turtle,
  Phone,
  PhoneOff,
  CalendarDays,
  RefreshCw,
  Award,
  Target,
  BarChart3,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { format } from 'date-fns';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const DATE_PRESETS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' },
];

const ExecutiveReportPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [report, setReport] = useState(null);
  const [cnrReport, setCnrReport] = useState(null);
  const [executives, setExecutives] = useState([]);
  const [selectedExecutive, setSelectedExecutive] = useState('all');
  const [datePreset, setDatePreset] = useState('last_7_days');
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [calendarOpen, setCalendarOpen] = useState(false);

  const fetchData = useCallback(async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      
      const params = new URLSearchParams();
      params.append('date_preset', datePreset);
      
      if (selectedExecutive && selectedExecutive !== 'all') {
        params.append('executive_id', selectedExecutive);
      }
      
      if (datePreset === 'custom' && customDateRange.from && customDateRange.to) {
        params.append('custom_start', customDateRange.from.toISOString());
        params.append('custom_end', customDateRange.to.toISOString());
      }
      
      const [reportRes, cnrRes, execRes] = await Promise.all([
        api.get(`/analytics/executive-conversion?${params.toString()}`),
        api.get(`/analytics/cnr-report?${params.toString()}`),
        api.get('/users/executives')
      ]);
      
      setReport(reportRes.data);
      setCnrReport(cnrRes.data);
      setExecutives(execRes.data || []);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, datePreset, selectedExecutive, customDateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCustomDateSelect = () => {
    if (customDateRange.from && customDateRange.to) {
      setCalendarOpen(false);
      fetchData();
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-16" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Prepare chart data
  const conversionChartData = report?.executives?.map(exec => ({
    name: exec.executiveName?.substring(0, 10) || 'Unknown',
    converted: exec.convertedLeads,
    rate: exec.conversionRate
  })) || [];

  const cnrChartData = cnrReport?.executiveCNR?.map(exec => ({
    name: exec.executiveName?.substring(0, 10) || 'Unknown',
    cnr: exec.totalCNR,
    calls: exec.totalCalls
  })) || [];

  return (
    <div className="space-y-6" data-testid="executive-report-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
            Executive Performance Report
          </h1>
          <p className="text-muted-foreground">
            Conversion timeline and CNR tracking by executive
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => fetchData(true)}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 p-4 bg-muted/30 rounded-lg border">
        <Select value={selectedExecutive} onValueChange={setSelectedExecutive}>
          <SelectTrigger className="w-[200px]" data-testid="executive-filter">
            <Users className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All Executives" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Executives</SelectItem>
            {executives.map(exec => (
              <SelectItem key={exec.id} value={exec.id}>
                {exec.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={datePreset} onValueChange={(v) => { setDatePreset(v); if (v === 'custom') setCalendarOpen(true); }}>
          <SelectTrigger className="w-[180px]" data-testid="date-filter">
            <CalendarDays className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Date Range" />
          </SelectTrigger>
          <SelectContent>
            {DATE_PRESETS.map(preset => (
              <SelectItem key={preset.value} value={preset.value}>
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {datePreset === 'custom' && (
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline">
                <CalendarDays className="h-4 w-4 mr-2" />
                {customDateRange.from && customDateRange.to 
                  ? `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d')}`
                  : 'Pick dates'
                }
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                selected={customDateRange}
                onSelect={setCustomDateRange}
                numberOfMonths={2}
              />
              <div className="p-3 border-t flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setCalendarOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleCustomDateSelect} disabled={!customDateRange.from || !customDateRange.to}>
                  Apply
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{report?.summary?.totalLeads || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {report?.summary?.totalConverted || 0} converted
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Conversion Rate</CardTitle>
            <Target className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {report?.summary?.overallConversionRate || 0}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Overall performance
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Avg Conversion Time</CardTitle>
            <Clock className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {report?.summary?.avgConversionTimeFormatted || '0h'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              From lead to conversion
            </p>
          </CardContent>
        </Card>

        <Card className="border-orange-200 bg-orange-50/30">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total CNR</CardTitle>
            <PhoneOff className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">
              {cnrReport?.totalCNR || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {cnrReport?.totalLeadsWithCNR || 0} leads with missed calls
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion by Executive */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Conversions by Executive
            </CardTitle>
            <CardDescription>Number of converted leads per executive</CardDescription>
          </CardHeader>
          <CardContent>
            {conversionChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={conversionChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="converted" fill="#10b981" radius={[4, 4, 0, 0]} name="Converted" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No conversion data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* CNR by Executive */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PhoneOff className="h-5 w-5 text-orange-500" />
              CNR by Executive
            </CardTitle>
            <CardDescription>Missed calls (Call Not Received) per executive</CardDescription>
          </CardHeader>
          <CardContent>
            {cnrChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={cnrChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="cnr" fill="#f97316" radius={[4, 4, 0, 0]} name="CNR" />
                  <Bar dataKey="calls" fill="#94a3b8" radius={[4, 4, 0, 0]} name="Total Calls" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No CNR data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Executive Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Executive Performance Details
          </CardTitle>
          <CardDescription>Conversion time, rate, and call metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Executive</TableHead>
                <TableHead className="text-center">Total Leads</TableHead>
                <TableHead className="text-center">Converted</TableHead>
                <TableHead className="text-center">Rate</TableHead>
                <TableHead className="text-center">Avg Time</TableHead>
                <TableHead className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Zap className="h-3 w-3 text-green-500" /> Fastest
                  </div>
                </TableHead>
                <TableHead className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Turtle className="h-3 w-3 text-red-500" /> Slowest
                  </div>
                </TableHead>
                <TableHead className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Phone className="h-3 w-3" /> Calls
                  </div>
                </TableHead>
                <TableHead className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <PhoneOff className="h-3 w-3 text-orange-500" /> CNR
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report?.executives?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No executive data available
                  </TableCell>
                </TableRow>
              ) : (
                report?.executives?.map((exec, index) => (
                  <TableRow key={exec.executiveId || index}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {index === 0 && exec.conversionRate > 0 && (
                          <Award className="h-4 w-4 text-yellow-500" />
                        )}
                        {exec.executiveName}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">{exec.totalLeads}</TableCell>
                    <TableCell className="text-center font-medium text-green-600">
                      {exec.convertedLeads}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={exec.conversionRate >= 20 ? 'success' : exec.conversionRate >= 10 ? 'warning' : 'secondary'}>
                        {exec.conversionRate}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center text-blue-600 font-medium">
                      {exec.avgConversionTimeFormatted}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-green-600">{exec.fastestConversionFormatted}</span>
                      {exec.fastestLead && (
                        <p className="text-xs text-muted-foreground truncate max-w-[80px]">
                          {exec.fastestLead.leadName}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-red-600">{exec.slowestConversionFormatted}</span>
                      {exec.slowestLead && (
                        <p className="text-xs text-muted-foreground truncate max-w-[80px]">
                          {exec.slowestLead.leadName}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-center">{exec.totalCalls}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                        {exec.cnrCount}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Top CNR Leads */}
      {cnrReport?.topCNRLeads?.length > 0 && (
        <Card className="border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-600">
              <PhoneOff className="h-5 w-5" />
              Top CNR Leads (Most Missed Calls)
            </CardTitle>
            <CardDescription>Leads with highest number of unanswered calls</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {cnrReport.topCNRLeads.slice(0, 10).map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-3 bg-orange-50/50 rounded-lg border border-orange-100">
                  <div>
                    <p className="font-medium">{lead.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {lead.mobile} • {lead.status} • Assigned: {lead.assignedTo}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Total Calls</p>
                      <p className="font-medium">{lead.totalCalls}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">CNR</p>
                      <Badge variant="destructive">{lead.cnrCount}</Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExecutiveReportPage;
